import React, { useMemo, useRef, useState } from 'react';
import type { DataTableSort } from '@ui/chart/DataTable';
import { Link } from 'react-router-dom';
import ButtonTabs from '@ui/tabs/ButtonTabs';
import CaptionGroup from '@ui/captiongroup/CaptionGroup';
import Message from '@ui/message/Message';
import KpiStrip from '@ui/chart/KpiStrip';
import DashboardPlaceholder from '@ui/placeholder/DashboardPlaceholder';
import { useRatePlanEvolution, useRatePlanReport } from '@app/model/bi/service/BiHooks';
import BiService, { saveFile } from '@app/model/bi/service/BiService';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import BiPageHeader from '../fragment/BiPageHeader';
import { buildUpLookup, RatePlanEvolutionCard, RatePlanMixCard, RatePlanTableCard } from '../fragment/RatePlanCards';
import {
	buildTree, ColumnSet, columnsOf, EvolutionMeasure, exportColumns, GroupBy, shareChange, tableRows, topRowOrder,
} from '../fragment/RatePlans.utilities';
import { exportFileName, formatDateTime, periodPresets, propertyCount, revenueOf, shortName } from '../Bi.utilities';
import { useAccountBiScope, useBiAddress } from '../BiScopeHooks';
import { useBiWidthClass } from '../BiFullWidth';
import BiSwitchRow from '../fragment/BiSwitchRow';
import useColumnSets from '../useColumnSets';

const GROUP_BY: { value: GroupBy, label: string }[] = [
	{ value: 'POLICY_BOARD', label: 'Policy & board' }, { value: 'POLICY', label: 'Refund policy' },
	{ value: 'BOARD', label: 'Board' }, { value: 'NONE', label: 'None' },
];
// Decision 7: TODAY (always), PREVIOUS YEAR and PU 7.
const DEFAULT_SETS: ColumnSet[] = ['ly', 'pu7'];
const ALL_SETS: ColumnSet[] = ['ly', 'd1', 'd3', 'd7', 'd14', 'd30', 'pu1', 'pu3', 'pu7', 'pu14', 'pu30'];

// RQ-05: this browser keeps the grouping. A read or a write can fail (a private window): then the page uses the default.
// ROO2-283: the user account keeps the column blocks; the choice of this browser from before (RQ-15) moves there once.
const GROUP_BY_KEY = 'roompulse.bi.ratePlans.groupBy';
const LEGACY_COLUMNS_KEY = 'roompulse.bi.ratePlans.columns';
const readStored = <T, >(key: string, valid: (v: unknown) => v is T, fallback: T): T => {
	try {
		const raw = window.localStorage.getItem(key);
		const value: unknown = raw === null ? null : JSON.parse(raw);
		return valid(value) ? value : fallback;
	} catch {
		return fallback;
	}
};
const store = (key: string, value: unknown) => {
	try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* the choice lives for this page only */ }
};
const isGroupBy = (v: unknown): v is GroupBy => GROUP_BY.some(g => g.value === v);

// The Rate Plans report (BI report 3, ROO2-263, direction B of the canvas of 2026-10-21): which rate plans bring the business of the
// hotel, grouped by refund policy and board, against last year. One period, "Stay months", and the revenue switches of the
// Segmentation report.
const BiRatePlansPage = () => {
	const scope = useAccountBiScope();
	const address = useBiAddress(scope.data);
	const [by, setBy] = useState<GroupBy>(() => readStored(GROUP_BY_KEY, isGroupBy, 'POLICY_BOARD'));
	const [sets, toggle] = useColumnSets('RATE_PLANS', ALL_SETS, DEFAULT_SETS, LEGACY_COLUMNS_KEY);
	const width = useBiWidthClass();
	const [mixBy, setMixBy] = useState<'POLICY' | 'BOARD'>('POLICY');
	// ROO2-267: the measure and the window of the build-up chart (RN and 30 days by default).
	const [measure, setMeasure] = useState<EvolutionMeasure>('rn');
	const [windowDays, setWindowDays] = useState(30);
	const toaster = useToaster();
	// true while the Excel file downloads: Export shows "Preparing…" and is disabled.
	const [exporting, setExporting] = useState(false);
	// false until the scope and the Property groups are known, so that a link with ?group= asks for no report of All properties first.
	const enabled = scope.data !== undefined && address.ready;
	const report = useRatePlanReport(address.companyHotelId, address.propertyGroupId, address.period.from, address.period.to, enabled);
	const evolution = useRatePlanEvolution(address.companyHotelId, address.propertyGroupId, address.period.from, address.period.to, windowDays,
		address.revenueScope, address.vat, enabled);
	// RQ-22: the focused row of the chart, and RQ-20: the sort of the table, which gives the order of the chart lines. Both
	// belong to one period, property or Property group, and grouping (resetKey): a change of these clears them, as it closes
	// the opened rows.
	const [focusOf, setFocusOf] = useState<{ key: string, id: string } | null>(null);
	const [sortOf, setSortOf] = useState<{ key: string, sort: DataTableSort | undefined } | null>(null);
	const chartRef = useRef<HTMLDivElement>(null);
	const { first, last } = address;
	const presets = useMemo(() => periodPresets(address.asOfMonth, first, last), [address.asOfMonth, first, last]);
	const s = scope.data;
	// More than one property: All properties or a Property group (the table and the file have property rows).
	const all = address.companyHotelId === null;
	const scopeR = address.revenueScope, vat = address.vat;
	const nameOf = useMemo(() => (id: number) => {
		const p = s?.properties.find(x => x.companyHotelId === id);
		return p === undefined || s === undefined ? String(id) : shortName(p, s.companyName);
	}, [s]);
	const data = report.data;
	const tree = useMemo(() => data === undefined ? undefined : buildTree(data, by, nameOf, all, scopeR, vat), [data, by, nameOf, all, scopeR, vat]);
	const buildUp = useMemo(() => {
		const lookup = buildUpLookup(evolution.data, by, measure, address.period);
		return lookup === null ? undefined : { block: `Build-up ${windowDays}D`, label: lookup.label, of: lookup.of };
	}, [evolution.data, by, measure, windowDays, address.period]);

	if (scope.isError) return <main className={`${width} px-4 py-6 sm:px-6 lg:px-8`}>
		<Message state="error" title="The Rate Plans report did not load" flush>Reload the page. If the problem stays, contact RoomPulse.</Message>
	</main>;
	if (s === undefined) return <main className={`${width} px-4 py-6 sm:px-6 lg:px-8`}><DashboardPlaceholder /></main>;

	// The properties of the selection: one property, the properties of a Property group, or all properties.
	const withData = address.properties.some(p => p.reservationsSource !== null);
	const sources = [...new Set(address.properties.map(p => p.reservationsSource).filter((v): v is string => v !== null))];
	const sublabel = all ? `${propertyCount(address.properties.length)} · Rate plans` : `${address.property?.area ?? ''} · Rate plans`;
	// The name of the scope in the file name: the property, the Property group, or null (All properties).
	const scopeName = address.property !== null ? shortName(address.property, s.companyName) : (address.propertyGroup?.title ?? null);
	const state = report.isError ? 'error' as const : (data === undefined ? 'loading' as const : (report.isPlaceholderData ? 'refreshing' as const : 'default' as const));
	const evolutionState = evolution.isError ? 'error' as const
		: (evolution.data === undefined ? 'loading' as const : (evolution.isPlaceholderData ? 'refreshing' as const : 'default' as const));
	// RQ-23, RQ-24: "Could not load" with the reason. GraphQLExecutor rejects with the message of the first GraphQL error.
	const reasonOf = (e: unknown): string | null => typeof e === 'string' ? e : (e instanceof Error ? e.message : null);
	const reportError = reasonOf(report.error);
	const evolutionError = reasonOf(evolution.error);
	const onBy = (value: GroupBy) => { setBy(value); store(GROUP_BY_KEY, value); };
	// RQ-03: "N rate plans · X of Y properties" (or the property's name).
	const readProperties = data === undefined ? 0 : new Set(data.parts.map(p => p.companyHotelId)).size;
	const scopeLine = data === undefined ? null
		: `${data.parts.length === 0 ? 0 : new Set(data.parts.map(p => p.key)).size} rate plans · ${all ? `${readProperties} of ${propertyCount(address.properties.length)}` : nameOf(address.companyHotelId as number)}`;
	// Decision 16: the properties with no stays in the last-year period stay out of Δ%. ROO2-271 D1: the table then shows today
	// like-for-like (TODAY LFL RN, TODAY LFL REV) and the tiles say "like-for-like".
	const missingNames = data === undefined ? [] : data.lastYearMissing.map(nameOf);
	const lfl = missingNames.length > 0;
	const leftOut = `${missingNames.length === 1 ? 'a property' : `${missingNames.length} properties`} with no stays in the last-year period: ${missingNames.join(', ')}`;
	const lastYearNote = !lfl ? null
		: `Like-for-like = today without ${leftOut}. TODAY LFL RN and TODAY LFL REV show it; Δ% and the change against STLY compare it with last year.`;
	const vsStly = lfl ? 'vs STLY, like-for-like' : 'vs STLY';
	const total = tree?.total;
	const rev = (f: Parameters<typeof revenueOf>[0]) => revenueOf(f, scopeR, vat);
	const nr = data === undefined ? null : shareChange(data, p => p.policy === 'NON_REFUNDABLE', scopeR, vat);
	const ro = data === undefined ? null : shareChange(data, p => p.board === 'ROOM_ONLY', scopeR, vat);
	const ratio = (a: number | null, b: number) => a === null || b === 0 ? null : a / b;
	// While the report loads, the four tiles show their labels over the loading skeleton of the strip; when it fails, "–"
	// (the cards below show the reason and Try again).
	const kpis = total === undefined || state === 'error' ? [
		{ id: 'rev', label: 'Revenue', value: null, format: 'money' as const },
		{ id: 'adr', label: 'ADR', value: null, format: 'money' as const, decimals: 2 },
		{ id: 'nr', label: 'Non-refundable share', value: null, format: 'percent' as const, decimals: 1 },
		{ id: 'ro', label: 'Room only share', value: null, format: 'percent' as const, decimals: 1 },
	] : [
		{ id: 'rev', label: 'Revenue', value: rev(total.cuts[0]), format: 'money' as const,
			change: (() => { const n = rev(total.comparable), l = rev(total.stly); return n === null || l === null || l === 0 ? undefined : { value: (n - l) / l, format: 'percent' as const, label: vsStly }; })() },
		{ id: 'adr', label: 'ADR', value: ratio(rev(total.cuts[0]), total.cuts[0].roomNights), format: 'money' as const, decimals: 2,
			change: (() => {
				const n = ratio(rev(total.comparable), total.comparable.roomNights), l = ratio(rev(total.stly), total.stly.roomNights);
				return n === null || l === null || l === 0 ? undefined : { value: (n - l) / l, format: 'percent' as const, label: vsStly };
			})() },
		{ id: 'nr', label: 'Non-refundable share', value: nr?.now ?? null, format: 'percent' as const, decimals: 1,
			change: nr?.points === null || nr?.points === undefined ? undefined : { value: nr.points, format: 'points' as const, label: vsStly, upIsGood: null } },
		{ id: 'ro', label: 'Room only share', value: ro?.now ?? null, format: 'percent' as const, decimals: 1,
			change: ro?.points === null || ro?.points === undefined ? undefined : { value: ro.points, format: 'points' as const, label: vsStly, upIsGood: null } },
	];
	// RQ-22: a change of the period, the property, the Property group or the grouping closes the opened rows and clears the focus.
	const resetKey = `${address.companyHotelId ?? `all${address.propertyGroupId ?? ''}`}|${address.period.from}|${address.period.to}|${by}`;
	const focus = focusOf?.key === resetKey ? focusOf.id : null;
	// With None the table sorts its top rows: the chart takes their order from the current rows, so a revenue switch, the
	// measure or the window, which the table sorts again, keeps both in step. Grouped, the groups keep their fixed order.
	const sort = sortOf?.key === resetKey ? sortOf.sort : undefined;
	const order = by === 'NONE' && sort !== undefined && tree !== undefined
		? topRowOrder(tableRows(tree, scopeR, vat, buildUp?.of), sort, columnsOf(sets, buildUp ?? null, lfl).columns) : null;
	const onFocus = (id: string | null) => setFocusOf(id === null ? null : { key: resetKey, id });
	// A click on a name in the table focuses the chart and scrolls to it; a second click on the same name returns to all lines.
	const onSelect = (row: { id: string }) => {
		if (row.id === focus) { onFocus(null); return; }
		onFocus(row.id);
		chartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
	};
	// The Excel file holds the table as the page shows it (decision 17). Export waits for the table: it is off while the table
	// loads, shows the old period dimmed, or failed.
	const exportReady = data !== undefined && !report.isPlaceholderData && !report.isError && !exporting;
	const exportFile = () => {
		setExporting(true);
		BiService.downloadRatePlanExcel({
			companyHotelId: address.companyHotelId, propertyGroupId: address.propertyGroupId, stayFrom: address.period.from, stayTo: address.period.to,
			groupBy: by, scope: scopeR, vat, columns: exportColumns(sets),
		})
			.then(blob => saveFile(blob, exportFileName(scopeName, address.period, s.asOf, 'Rate plans')))
			.catch(() => toaster.error('Export', 'The Excel file did not download. Try again. If the problem stays, contact RoomPulse.'))
			.finally(() => setExporting(false));
	};

	return <>
		<BiPageHeader scope={s} companyHotelId={address.companyHotelId} propertyGroup={address.propertyGroup} groups={address.groups}
			canEditGroups={address.canEditGroups} search={address.search} sublabel={sublabel} page="Rate plans" fullWidthSwitch>
			{address.importedAt !== null && <span className="whitespace-nowrap text-sm text-gray-500">Reservations as of{' '}
				<span className="font-semibold text-gray-900">{formatDateTime(address.importedAt)}</span></span>}
		</BiPageHeader>
		<main className={`${width} flex flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8`}>
			{!withData && <Message state="info" title="No reservations for this scope" flush>
				<p>The Rate Plans report reads the rate plans of the imported reservations. Connect a Reservations integration in{' '}
					<Link to={all ? '/secured/settings/integrations' : `/secured/settings/integrations/${address.companyHotelId ?? ''}#reservations`}
						className="font-medium underline">Settings › Integrations</Link>.</p>
			</Message>}
			{withData && <>
				<BiSwitchRow scope={scopeR} vat={vat} onScope={v => address.set({ revenue: v === 'TOTAL' ? 'total' : 'room' })}
					onVat={v => address.set({ vat: v === 'NET' ? 'net' : 'incl' })} period={address.period}
					onPeriod={range => address.set({ from: range.from, to: range.to })} presets={presets} min={first} max={last}
					line={scopeLine} sources={sources}>
					<CaptionGroup caption="Group by" className="col-span-2 w-full sm:col-span-1 sm:w-auto">
						<div className="w-full min-w-0">
							<ButtonTabs label="Group the rate plans by" value={by} onChange={v => onBy(v as GroupBy)} options={GROUP_BY} />
						</div>
					</CaptionGroup>
				</BiSwitchRow>
				{/* KpiStrip has no error or refreshing state: the page dims it while a new period loads, as the cards. */}
				<div aria-busy={state === 'refreshing'} className={`transition-opacity${state === 'refreshing' ? ' opacity-50' : ''}`}>
					<KpiStrip ariaLabel="Rate plans: headline figures" items={kpis} state={state === 'loading' ? 'loading' : 'default'} />
					{/* ROO2-271 D1-c: KpiStrip shows the change label only as the chip tooltip, so the page says it in one line. */}
					{lfl && <p className="mt-2 text-xs text-gray-500">The changes are like-for-like: they leave out {leftOut}.</p>}
				</div>
				<RatePlanMixCard report={data} by={mixBy} onBy={setMixBy} period={address.period} scope={scopeR} vat={vat} state={state}
					onRetry={() => void report.refetch()} lastYearNote={lastYearNote} error={reportError} />
				<RatePlanTableCard tree={tree} by={by} period={address.period} sets={sets} onToggle={toggle} scope={scopeR} vat={vat} state={state}
					onRetry={() => void report.refetch()} resetKey={resetKey} lastYearNote={lastYearNote} lfl={lfl}
					onExport={exportFile} exporting={exporting} exportReady={exportReady} error={reportError}
					buildUp={buildUp} onSelect={onSelect} selectedId={focus ?? undefined} onSortChange={next => setSortOf({ key: resetKey, sort: next })} />
				<RatePlanEvolutionCard ref={chartRef} evolution={evolution.data} tree={tree} by={by} period={address.period} measure={measure}
					onMeasure={setMeasure} window={windowDays} onWindow={setWindowDays} state={evolutionState} error={evolutionError}
					onRetry={() => void evolution.refetch()} order={order} focus={focus} onFocus={onFocus} />
			</>}
		</main>
	</>;
};

export default BiRatePlansPage;
